import { useMemo, useState, Dispatch } from 'react';

import { HttpError } from 'lib/errors';

type Metadata = {
  presignedUrl: string;
  key: string;
  fields?: { [key: string]: string };
};

function buildUpload(
  setLoading: Dispatch<boolean>,
  setUploadPercentage: Dispatch<number>,
) {
  return async (file: File, metadata: Metadata) => {
    setLoading(true);
    const body = new FormData();

    if (metadata.fields) {
      const { fields } = metadata;

      Object.keys(fields).forEach((key) => {
        const field = fields[key];
        if (!field) {
          return;
        }
        body.append(key, field);
      });
    }

    body.append('file', file);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', metadata.presignedUrl);

      xhr.upload.onprogress = function (event) {
        const uploadPercentage = Math.ceil((event.loaded / event.total) * 100);
        setUploadPercentage(uploadPercentage);
      };

      xhr.upload.onerror = function () {
        setLoading(false);
        const error = new HttpError({
          message: 'There was a problem uploading your file.',
          status: xhr.status,
          statusText: xhr.statusText,
        });
        reject(error);
      };

      xhr.upload.onload = function () {
        setLoading(false);
        resolve(xhr.response);
      };

      xhr.send(body);
    });
  };
}

export function useAmazonS3() {
  const [loading, setLoading] = useState(false);
  const [uploadPercentage, setUploadPercentage] = useState(0);

  return {
    loading,
    uploadPercentage,
    upload: useMemo(() => buildUpload(setLoading, setUploadPercentage), []),
  };
}
